import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { ActivityApi, ActivityRequestDto, ActivityResponseDto } from "../api/client-axios";
import { ApiError } from "../model/apiError";
import { activityFieldKeys } from "../model/localizedEntities";
import { Problem } from "../model/problem";
import { pruneTranslations } from "../utils/translations";
import { addAsyncCases, apiRejection } from "./apiThunk";
import { apiConfiguration } from "./server";

export interface ActivityState {
    activities?: ActivityResponseDto[],
    selectedActivity?: ActivityResponseDto,
    status: "idle" | "loading" | "error" | "adding" | "success" | "auth-error" | "deleting" ;
    error?: string | Problem;
    code?: number;
}

const initialActivityState: ActivityState = {
    status: "idle",
    error: ""
}

/**
 * Narrows an edited activity to what the API accepts: the response-only `id` is
 * dropped and translation rows the admin left blank are pruned, so clearing a
 * translation deletes it server-side.
 *
 * `translations` is omitted entirely when the record carries none, keeping the
 * payload identical to the pre-localization one against backends that do not
 * support translations yet.
 */
export function toActivityRequest(activity: ActivityResponseDto): ActivityRequestDto {
    const { name, translations } = pruneTranslations(activity, activityFieldKeys);
    return translations == null ? { name } : { name, translations };
}

export const fetchActivities = createAsyncThunk<ActivityResponseDto[], void, { rejectValue: ApiError }>('activities/fetchActivities', async (_, thunkApi) => {
    try {
        const api = new ActivityApi(apiConfiguration());
        const response = await api.apiV1ActivityGet();
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});

export const addActivity = createAsyncThunk<ActivityResponseDto[], ActivityResponseDto, { rejectValue: ApiError }>('activities/addActivity', async (activity, thunkApi) => {
    try {
        const api = new ActivityApi(apiConfiguration());
        await api.apiV1ActivityPut(toActivityRequest(activity));
        const response = await api.apiV1ActivityGet();
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});

export const updateActivity = createAsyncThunk<ActivityResponseDto[], ActivityResponseDto, { rejectValue: ApiError }>('activities/updateActivity', async (activity, thunkApi) => {
    try {
        const api = new ActivityApi(apiConfiguration());
        await api.apiV1ActivityIdPost(activity.id!, toActivityRequest(activity));
        const response = await api.apiV1ActivityGet();
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});

export const deleteActivity = createAsyncThunk<ActivityResponseDto[], string, { rejectValue: ApiError }>('activities/deleteActivity', async (id, thunkApi) => {
    try {
        const api = new ActivityApi(apiConfiguration());
        await api.apiV1ActivityIdDelete(id);
        const response = await api.apiV1ActivityGet();
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});


export const selectActivity = createAction("activities/selectActivity", (activity?: ActivityResponseDto) => ({ payload: activity }));
export const clearErrorActivity = createAction("activities/clearErrorActivity");
export const startAddActivity = createAction("activities/startAddActivity", (activity?: ActivityResponseDto) => ({ payload: activity }));
export const startDeleteActivity = createAction("activities/startDeleteActivity");


const activitiesSlice = createSlice({
    name: "activities",
    initialState: initialActivityState,
    reducers: {},
    extraReducers: builder => {
        addAsyncCases(
            builder,
            [fetchActivities, addActivity, updateActivity, deleteActivity],
            (state, activities) => {
                state.status = "success";
                state.activities = activities;
            }
        );

        builder
            .addCase(selectActivity, (state, action) => {
                state.selectedActivity = action.payload;
            })
            .addCase(clearErrorActivity, (state) => {
                state.status = 'success';
            })
            .addCase(startAddActivity, (state, action) => {
                state.status = 'adding';
                state.selectedActivity = action.payload;
            })
            .addCase(startDeleteActivity, (state) => {
                state.status = 'deleting';
            });
    }
});

export default activitiesSlice.reducer;
